// Application Insights Action Constants
// ===================================
// Centralized constants for all Application Insights tracking actions
// This ensures consistency across components and makes refactoring easier

export const AppInsightsActions = {
  // Authentication Actions
  LOGIN_ATTEMPT: "login-attempt",
  LOGIN_SUCCESS: "login-success", 
  LOGIN_FAILURE: "login-failure",
  LOGOUT_ATTEMPT: "logout-attempt",
  LOGOUT_SUCCESS: "logout-success",
  LOGOUT_FAILURE: "logout-failure",
  LOGOUT_API_FAILURE: "logout-api-failure",

  // Password Management Actions
  PASSWORD_RESET_ATTEMPT: "password-reset-attempt",
  PASSWORD_RESET_SUCCESS: "password-reset-success",
  PASSWORD_RESET_FAILURE: "password-reset-failure",
  PASSWORD_CHANGE_ATTEMPT: "password-change-attempt",
  PASSWORD_CHANGE_SUCCESS: "password-change-success",
  PASSWORD_CHANGE_FAILURE: "password-change-failure",

  // Registration Actions
  PAGE_VISIT: "page-visit",
  NAVIGATION_FORGOT_PASSWORD: "navigation-forgot-password",
  NAVIGATION_ALREADY_HAVE_ACCOUNT: "navigation-already-have-account",
  NAVIGATION_FAILURE: "navigation-failure",
  CANCEL_BUTTON_CLICK: "cancel-button-click",
  CANCEL_FAILURE: "cancel-failure",
  IFRAME_LOAD_SUCCESS: "iframe-load-success",
  IFRAME_LOAD_FAILURE: "iframe-load-failure",

  // Product Selection Actions
  PRODUCT_NAVIGATION: "product-navigation",
  NAVIGATION_FAILURE_PRODUCT: "navigation-failure",
  NO_SUBSCRIPTIONS: "no-subscriptions",
  SEASCAPE_BUTTON_FAILURE: "seascape-button-failure",
  HORIZON_BUTTON_FAILURE: "horizon-button-failure",

  // API and Network Actions
  API_REQUEST: "api-request",
  API_RESPONSE: "api-response",
  API_RESPONSE_ERROR: "api-response-error",
  API_REQUEST_FAILURE: "api-request-failure",
  API_REQUEST_SUCCESS: "api-request-success",
  TOKEN_REFRESH_ATTEMPT: "token-refresh-attempt",
  TOKEN_REFRESH_SUCCESS: "token-refresh-success",
  TOKEN_REFRESH_FAILURE: "token-refresh-failure",
  UNAUTHORIZED_ACCESS: "unauthorized-access",
  NETWORK_ERROR: "network-error",

  // Contact/Feedback Actions
  FEEDBACK_SUBMISSION_ATTEMPT: "feedback-submission-attempt",
  FEEDBACK_SUBMISSION_SUCCESS: "feedback-submission-success",
  FEEDBACK_SUBMISSION_FAILURE: "feedback-submission-failure",

  // Session Management Actions
  SESSION_CHECK_ATTEMPT: "session-check-attempt",
  SESSION_CHECK_SUCCESS: "session-check-success",
  SESSION_CHECK_FAILURE: "session-check-failure",
  SESSION_SYNC_FAILURE: "session-sync-failure",

  // User Info Actions
  USER_INFO_FETCH_ATTEMPT: "user-info-fetch-attempt",
  USER_INFO_FETCH_SUCCESS: "user-info-fetch-success",
  USER_INFO_FETCH_FAILURE: "user-info-fetch-failure",

  // Authentication Hook Actions
  AUTH_CHECK_FAILURE: "auth-check-failure",
  AUTH_STATUS_CHANGE: "auth-status-change",

  // Invitation Actions
  INVITE_VALIDATION_ATTEMPT: "invite-validation-attempt",
  INVITE_VALIDATION_SUCCESS: "invite-validation-success",
  INVITE_VALIDATION_FAILURE: "invite-validation-failure",
  INVITE_ACCEPT_ATTEMPT: "invite-accept-attempt",
  INVITE_ACCEPT_SUCCESS: "invite-accept-success",
  INVITE_ACCEPT_FAILURE: "invite-accept-failure",
  INVITE_PASSWORD_VALIDATION_FAILURE: "invite-password-validation-failure",

  // General Navigation Actions
  NAVIGATION_SUCCESS: "navigation-success",
  COMPONENT_MOUNT: "component-mount",
  COMPONENT_ERROR: "component-error"
} as const;

// Component Names for consistent tracking
export const AppInsightsComponents = {
  LOGIN: "Login",
  LOGOUT: "Logout",
  REGISTER: "Register",
  FORGOT_PASSWORD: "ForgotPassword",
  CHANGE_PASSWORD: "ChangePassword",
  PRODUCT_SELECTION: "ProductSelection",
  CONTACT_US_MODAL: "ContactUsModal",
  USER_PROFILE_MENU: "UserProfileMenu",
  AXIOS_INTERCEPTOR: "AxiosInterceptor",
  AXIOS_SEASCAPE_INTERCEPTOR: "AxiosSeascapeInterceptor",
  USE_AUTH_HOOK: "useAuth",
  SESSION_SYNC: "SessionSync",
  PRODUCT_LAYOUT: "ProductLayout",
  USER_ACCESS_LAYOUT: "UserAccessLayout",
  AXIOS_INSTANCE: "AxiosInstance",
  AXIOS_SEASCAPE_INSTANCE: "AxiosSeascapeInstance",
  INVITE_PAGE: "InvitePage",
  USE_INVITATION_HOOK: "useInvitation",
  INVITE_API: "InviteApi"
} as const;

// Error Types for consistent categorization
export const AppInsightsErrorTypes = {
  API_ERROR: "api-error",
  NETWORK_ERROR: "network-error",
  VALIDATION_ERROR: "validation-error",
  AUTHENTICATION_ERROR: "authentication-error",
  AUTH_ERROR: "auth-error",
  AUTHORIZATION_ERROR: "authorization-error",
  CONFIGURATION_ERROR: "configuration-error",
  UNEXPECTED_ERROR: "unexpected-error",
  USER_INPUT_ERROR: "user-input-error",
  EXTERNAL_SERVICE_ERROR: "external-service-error",
  INVITE_VALIDATION_ERROR: "invite-validation-error",
  INVITE_EXPIRED_ERROR: "invite-expired-error",
  INVITE_USED_ERROR: "invite-used-error",
  INVITE_REVOKED_ERROR: "invite-revoked-error"
} as const;

// Navigation Reasons for Product Selection
export const AppInsightsNavigationReasons = {
  SINGLE_PRODUCT_SEASCAPE_DESKTOP: "single-product-seascape-desktop",
  SINGLE_PRODUCT_HORIZON_OR_MOBILE: "single-product-horizon-or-mobile", 
  MANUAL_SELECTION_SEASCAPE: "manual-selection-seascape",
  MANUAL_SELECTION_HORIZON: "manual-selection-horizon"
} as const;

// Type exports for TypeScript support
export type AppInsightsAction = typeof AppInsightsActions[keyof typeof AppInsightsActions];
export type AppInsightsComponent = typeof AppInsightsComponents[keyof typeof AppInsightsComponents];
export type AppInsightsErrorType = typeof AppInsightsErrorTypes[keyof typeof AppInsightsErrorTypes];
export type AppInsightsNavigationReason = typeof AppInsightsNavigationReasons[keyof typeof AppInsightsNavigationReasons];